import React, { useState } from 'react';
import { Settings as SettingsIcon, Cpu, Zap, AlertTriangle } from 'lucide-react';
import { useTrading, useConnection, useMarketData, useEngine } from '../context/MarketContext';
import type { EngineRegime, EngineConfig } from '../types';
import { INSTRUMENTS } from '../constants/instruments';

const REGIME_COLORS: Record<EngineRegime, string> = {
  ACTIVE: '#36c98e', QUIET: '#4da3ff', DORMANT: '#6b7280', VOLATILE: '#ff5c5c',
  accumulation: '#c77dff', distribution: '#ff8c3a', neutral: '#7a8693',
};
const REGIME_LABEL: Record<EngineRegime, string> = {
  ACTIVE: 'ACTIVE', QUIET: 'QUIET', DORMANT: 'DORMANT', VOLATILE: 'VOLATILE',
  accumulation: 'ACCUM', distribution: 'DIST', neutral: 'NEUTRAL',
};

function Toggle({ on, onToggle }: { on: boolean; onToggle: () => void }) {
  return (
    <button onClick={onToggle} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', background: 'none', border: 'none', padding: 0 }}>
      <div style={{ width: 44, height: 24, borderRadius: 12, position: 'relative', backgroundColor: on ? '#36c98e' : '#2a3540', transition: 'background-color 0.2s' }}>
        <div style={{ position: 'absolute', top: 3, left: on ? 23 : 3, width: 18, height: 18, borderRadius: '50%', backgroundColor: '#fff', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.4)' }} />
      </div>
      <span style={{ fontSize: 12, fontWeight: 700, color: on ? '#36c98e' : '#7e8a96' }}>{on ? 'ON' : 'OFF'}</span>
    </button>
  );
}

function BtnGroup<T extends string>({ options, value, onChange }: { options: { label: string; value: T }[]; value: T; onChange: (v: T) => void }) {
  return (
    <div style={{ display: 'flex', gap: 4 }}>
      {options.map(o => (
        <button key={o.value} onClick={() => onChange(o.value)} style={{
          padding: '4px 10px', borderRadius: 4, fontSize: 11, fontWeight: 700, cursor: 'pointer',
          background: value === o.value ? '#4da3ff' : '#0a0d10',
          color: value === o.value ? '#fff' : '#7a8693',
          border: `1px solid ${value === o.value ? '#4da3ff' : '#2a3540'}`,
          transition: 'all 0.15s',
        }}>{o.label}</button>
      ))}
    </div>
  );
}

export default function SettingsPage() {
  const { isAutoTrading, setIsAutoTrading } = useTrading();
  const { selectedSymbol, analytics } = useMarketData();
  const { connectionStatus } = useConnection();
  const { regimeBySymbol, sprintState, sendEngineConfig, engineConfig, configAckStatus, configAckTime, configAckErrors } = useEngine();

  const [quoteTTL, setQuoteTTL] = useState(5);

  // Local engine config form state (only sent when user clicks Apply)
  const [localConfig, setLocalConfig] = useState<Partial<EngineConfig>>({
    initial_capital: engineConfig?.initial_capital ?? 50_000_000,
    ml_enabled: engineConfig?.ml_enabled ?? false,
    market_making_enabled: engineConfig?.market_making_enabled ?? false,
    market_making_mode: engineConfig?.market_making_mode ?? 'manual',
    ml_model_type: engineConfig?.ml_model_type ?? 'heuristic',
    mode: engineConfig?.mode ?? 'SIMULATOR',
    settlement_mode: engineConfig?.settlement_mode ?? 'instant',
  });

  const card = { background: '#10151b', borderRadius: 8, border: '1px solid #2a3540', padding: 16 };
  const connColor = connectionStatus === 'connected' ? '#36c98e' : connectionStatus === 'connecting' ? '#ff8c3a' : '#ff5c5c';
  const connLabel = connectionStatus === 'connected' ? 'LIVE' : connectionStatus === 'connecting' ? 'SYNCING' : 'OFFLINE';

  const staleness = analytics?.staleness ?? null;
  const isFresh = staleness ? !staleness.isStale : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <SettingsIcon size={16} color="#7e8a96" />
        <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#d7dde4' }}>Settings</h2>
      </div>

      {/* Card 1 — Trading Configuration */}
      <div style={card}>
        <SectionLabel>TRADING CONFIGURATION</SectionLabel>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          <ConfigRow label="Auto-Trading">
            <button
              onClick={() => setIsAutoTrading(!isAutoTrading)}
              style={{
                display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer',
                background: 'none', border: 'none', padding: 0,
              }}
            >
              <div style={{
                width: 44, height: 24, borderRadius: 12, position: 'relative', cursor: 'pointer',
                backgroundColor: isAutoTrading ? '#36c98e' : '#2a3540',
                transition: 'background-color 0.2s',
              }}>
                <div style={{
                  position: 'absolute', top: 3, left: isAutoTrading ? 23 : 3,
                  width: 18, height: 18, borderRadius: '50%', backgroundColor: '#fff',
                  transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.4)',
                }} />
              </div>
              <span style={{
                fontSize: 12, fontWeight: 700,
                color: isAutoTrading ? '#36c98e' : '#7e8a96',
              }}>
                {isAutoTrading ? 'ON' : 'OFF'}
              </span>
            </button>
          </ConfigRow>
          <ConfigRow label="Active Symbol">
            <span style={{
              padding: '3px 10px', borderRadius: 4, background: '#0a0d10',
              color: '#d7dde4', fontSize: 12, fontWeight: 700, border: '1px solid #2a3540',
            }}>
              {selectedSymbol || '—'}
            </span>
          </ConfigRow>
          <ConfigRow label={<>Quote TTL <span style={{ color:'#46525e', fontSize:10 }}>(local preference)</span></>}>
            <input
              type="number"
              value={quoteTTL}
              onChange={e => setQuoteTTL(Number(e.target.value))}
              min={1} max={60}
              style={{
                width: 72, padding: '4px 8px', background: '#0a0d10', color: '#d7dde4',
                border: '1px solid #2a3540', borderRadius: 6, fontSize: 13, textAlign: 'right',
              }}
            />
            <span style={{ fontSize:12, color:'#46525e', marginLeft:4 }}>s</span>
          </ConfigRow>
          <ConfigRow label="Dark Theme">
            <span style={{ fontSize: 12, color: '#46525e' }}>Always on</span>
          </ConfigRow>
        </div>
      </div>

      {/* Card 2 — Engine Runtime Configuration */}
      <div style={card}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
          <Cpu size={13} color="#7e8a96" />
          <SectionLabelInline>ENGINE CONFIGURATION</SectionLabelInline>
          {configAckStatus === 'pending' && (
            <span style={{ marginLeft: 'auto', fontSize: 10, color: '#ff8c3a', fontWeight: 700 }}>● PENDING…</span>
          )}
          {configAckStatus === 'ok' && configAckTime && (
            <span style={{ marginLeft: 'auto', fontSize: 10, color: '#36c98e', fontWeight: 700 }}>
              ✓ APPLIED {configAckTime.slice(11, 19)}
            </span>
          )}
          {configAckStatus === 'error' && (
            <span style={{ marginLeft: 'auto', fontSize: 10, color: '#ff5c5c', fontWeight: 700 }}
                  title={configAckErrors ? Object.entries(configAckErrors).map(([k, v]) => `${k}: ${v}`).join('\n') : undefined}>
              ✗ ERROR{configAckErrors ? `: ${Object.keys(configAckErrors).join(', ')}` : ''}
            </span>
          )}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          <ConfigRow label="Mode">
            <BtnGroup
              options={[
                { label: 'Simulator', value: 'SIMULATOR' },
                { label: 'DSE', value: 'REAL_DSE' },
                { label: 'Hetzner', value: 'HETZNER' },
              ] as { label: string; value: EngineConfig['mode'] }[]}
              value={(localConfig.mode ?? 'SIMULATOR') as EngineConfig['mode']}
              onChange={v => setLocalConfig(p => ({ ...p, mode: v }))}
            />
          </ConfigRow>
          <ConfigRow label={<>Initial Cash <span style={{ color: '#46525e', fontSize: 10 }}>(TZS)</span></>}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <input
                type="number"
                value={Math.round((localConfig.initial_capital ?? 50_000_000) / 1_000_000)}
                onChange={e => setLocalConfig(p => ({ ...p, initial_capital: Number(e.target.value) * 1_000_000 }))}
                min={1} max={10000} step={1}
                style={{ width: 80, padding: '4px 8px', background: '#0a0d10', color: '#d7dde4', border: '1px solid #2a3540', borderRadius: 6, fontSize: 13, textAlign: 'right' }}
              />
              <span style={{ fontSize: 12, color: '#46525e' }}>M</span>
            </div>
          </ConfigRow>
          <ConfigRow label="Market Making">
            <Toggle
              on={localConfig.market_making_enabled ?? false}
              onToggle={() => setLocalConfig(p => ({ ...p, market_making_enabled: !p.market_making_enabled }))}
            />
          </ConfigRow>
          <ConfigRow label="MM Mode">
            <BtnGroup
              options={[
                { label: 'Manual', value: 'manual' },
                { label: 'Automated', value: 'automated' },
              ] as { label: string; value: EngineConfig['market_making_mode'] }[]}
              value={(localConfig.market_making_mode ?? 'manual') as EngineConfig['market_making_mode']}
              onChange={v => setLocalConfig(p => ({ ...p, market_making_mode: v }))}
            />
          </ConfigRow>
          <ConfigRow label="ML Enabled">
            <Toggle
              on={localConfig.ml_enabled ?? false}
              onToggle={() => setLocalConfig(p => ({ ...p, ml_enabled: !p.ml_enabled }))}
            />
          </ConfigRow>
          <ConfigRow label="ML Model">
            <BtnGroup
              options={[
                { label: 'Heuristic', value: 'heuristic' },
                { label: 'River', value: 'river' },
                { label: 'RF', value: 'rf' },
              ] as { label: string; value: EngineConfig['ml_model_type'] }[]}
              value={(localConfig.ml_model_type ?? 'heuristic') as EngineConfig['ml_model_type']}
              onChange={v => setLocalConfig(p => ({ ...p, ml_model_type: v }))}
            />
          </ConfigRow>
          <ConfigRow label={<>Settlement <span style={{ color: '#46525e', fontSize: 10 }}>(restart to apply)</span></>}>
            <BtnGroup
              options={[
                { label: 'Instant', value: 'instant' },
                { label: 'Realistic T+2', value: 'realistic' },
              ] as { label: string; value: EngineConfig['settlement_mode'] }[]}
              value={(localConfig.settlement_mode ?? 'instant') as EngineConfig['settlement_mode']}
              onChange={v => setLocalConfig(p => ({ ...p, settlement_mode: v }))}
            />
          </ConfigRow>
        </div>

        <div style={{ marginTop: 14, display: 'flex', justifyContent: 'flex-end' }}>
          <button
            onClick={() => sendEngineConfig(localConfig)}
            disabled={connectionStatus !== 'connected'}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '7px 18px', borderRadius: 6, fontSize: 12, fontWeight: 700,
              background: connectionStatus === 'connected' ? '#4da3ff' : '#2a3540',
              color: connectionStatus === 'connected' ? '#fff' : '#7a8693',
              border: 'none', cursor: connectionStatus === 'connected' ? 'pointer' : 'not-allowed',
              transition: 'all 0.15s',
            }}
          >
            <Zap size={13} />
            Apply to Engine
          </button>
        </div>
      </div>

      {/* Emergency Stop */}
      <EmergencyStop sendEngineConfig={sendEngineConfig} connectionStatus={connectionStatus} />

      {/* Card 3 — Engine Status */}
      <div style={card}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <SectionLabelInline>ENGINE STATUS</SectionLabelInline>
          <span style={{
            padding: '2px 10px', borderRadius: 4,
            backgroundColor: `${connColor}22`, color: connColor,
            fontSize: 10, fontWeight: 700, letterSpacing: '0.05em',
          }}>
            ● {connLabel}
          </span>
        </div>

        {/* Regime Multipliers */}
        {regimeBySymbol.size > 0 ? (
          <>
            <div style={{ fontSize: 10, color: '#46525e', fontWeight: 700, letterSpacing: '0.06em', marginBottom: 8 }}>
              REGIME MULTIPLIERS
            </div>
            <div style={{
              display: 'grid', gridTemplateColumns: '80px 1fr 80px 80px',
              gap: 8, padding: '5px 0', borderBottom: '1px solid #2a3540',
              fontSize: 10, fontWeight: 700, color: '#46525e', letterSpacing: '0.06em',
            }}>
              <div>SYMBOL</div><div>REGIME</div>
              <div style={{ textAlign: 'right' }}>SPREAD ×</div>
              <div style={{ textAlign: 'right' }}>SIZE ×</div>
            </div>
            {Array.from(regimeBySymbol.entries()).map(([ticker, rs]) => {
              const rc = REGIME_COLORS[rs.regime as EngineRegime] ?? '#7a8693';
              const rl = REGIME_LABEL[rs.regime as EngineRegime] ?? rs.regime;
              return (
                <div key={ticker} style={{
                  display: 'grid', gridTemplateColumns: '80px 1fr 80px 80px',
                  gap: 8, padding: '7px 0', borderBottom: '1px solid #0a0d10', alignItems: 'center',
                }}>
                  <div style={{ fontWeight: 700, fontSize: 12, color: '#d7dde4' }}>{ticker}</div>
                  <div>
                    <span style={{
                      display: 'inline-block', padding: '2px 8px', borderRadius: 4,
                      backgroundColor: `${rc}22`, color: rc,
                      fontSize: 10, fontWeight: 700, letterSpacing: '0.05em',
                    }}>{rl}</span>
                  </div>
                  <div style={{ textAlign: 'right', fontSize: 12, color: '#d7dde4' }}>
                    ×{(rs.spread_multiplier ?? 1).toFixed(1)}
                  </div>
                  <div style={{ textAlign: 'right', fontSize: 12, color: '#d7dde4' }}>
                    ×{(rs.size_multiplier ?? 1).toFixed(1)}
                  </div>
                </div>
              );
            })}
          </>
        ) : (
          <div style={{ fontSize: 12, color: '#46525e', marginBottom: 12 }}>
            No regime data — engine not connected
          </div>
        )}

        {/* Sprint Window */}
        {sprintState && (
          <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid #2a3540' }}>
            <div style={{ fontSize: 10, color: '#46525e', fontWeight: 700, letterSpacing: '0.06em', marginBottom: 8 }}>
              SPRINT WINDOW
            </div>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
              <div style={{ fontSize: 12, color: '#7e8a96' }}>
                {sprintState.sprint_start.slice(0, 10)} → {sprintState.sprint_end.slice(0, 10)}
              </div>
              <div style={{ fontSize: 12, color: '#7a8693' }}>
                {sprintState.winners.length} winner{sprintState.winners.length !== 1 ? 's' : ''}
              </div>
              {sprintState.winners.length > 0 && (
                <div style={{ display: 'flex', gap: 6 }}>
                  {sprintState.winners.map(w => (
                    <span key={w} style={{
                      padding: '2px 8px', borderRadius: 4, background: '#ff8c3a22',
                      color: '#ff8c3a', fontSize: 10, fontWeight: 700,
                    }}>{w}</span>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Card 4 — Instruments */}
      <div style={card}>
        <SectionLabel>INSTRUMENTS · {INSTRUMENTS.length} configured</SectionLabel>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {(INSTRUMENTS as { secId: string; label: string }[]).map(inst => (
            <span key={inst.secId} style={{
              padding: '4px 10px', borderRadius: 4, background: '#0a0d10',
              border: '1px solid #2a3540', color: '#d7dde4', fontSize: 11, fontWeight: 600,
            }}>
              {inst.label}
            </span>
          ))}
        </div>
      </div>

      {/* Card 5 — Session Info */}
      <div style={card}>
        <SectionLabel>SESSION INFO</SectionLabel>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          <ConfigRow label="Today (EAT)">
            <span style={{ fontSize: 12, color: '#d7dde4' }}>
              {new Date().toLocaleDateString('en-TZ', { timeZone: 'Africa/Nairobi', weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' })}
            </span>
          </ConfigRow>
          <ConfigRow label="Market Hours">
            <span style={{ fontSize: 12, color: '#7e8a96' }}>DSE: 09:30–15:00 EAT (Mon–Fri)</span>
          </ConfigRow>
          <ConfigRow label="Data Freshness">
            {isFresh !== null ? (
              <span style={{
                padding: '2px 10px', borderRadius: 4,
                backgroundColor: isFresh ? '#36c98e22' : '#ff5c5c22',
                color: isFresh ? '#36c98e' : '#ff5c5c',
                fontSize: 10, fontWeight: 700,
              }}>
                {isFresh ? '● FRESH' : '● STALE'}
              </span>
            ) : (
              <span style={{ fontSize: 12, color: '#46525e' }}>—</span>
            )}
          </ConfigRow>
          <ConfigRow label="Dashboard">
            <span style={{ fontSize: 12, color: '#46525e' }}>Shabba PC Engine v1.0</span>
          </ConfigRow>
        </div>
      </div>
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ fontSize: 11, color: '#46525e', fontWeight: 700, letterSpacing: '0.06em', marginBottom: 12 }}>
      {children}
    </div>
  );
}

function SectionLabelInline({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ fontSize: 11, color: '#46525e', fontWeight: 700, letterSpacing: '0.06em' }}>
      {children}
    </div>
  );
}

function EmergencyStop({
  sendEngineConfig,
  connectionStatus,
}: {
  sendEngineConfig: (cfg: Partial<EngineConfig & { kill_switch: boolean }>) => void;
  connectionStatus: string;
}) {
  const [confirmed, setConfirmed] = React.useState(false);
  const [fired, setFired] = React.useState(false);

  const handleStop = () => {
    if (!confirmed) {
      setConfirmed(true);
      // Auto-reset confirmation after 4s if not clicked again
      setTimeout(() => setConfirmed(false), 4000);
      return;
    }
    sendEngineConfig({ kill_switch: true, market_making_enabled: false });
    setFired(true);
    setConfirmed(false);
  };

  return (
    <div style={{
      background: fired ? '#0f2520' : '#1a0e0e',
      border: `1px solid ${fired ? '#36c98e' : confirmed ? '#ff5c5c' : '#7f1d1d'}`,
      borderRadius: 8,
      padding: 16,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <AlertTriangle size={14} color={fired ? '#36c98e' : '#ff5c5c'} />
        <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', color: fired ? '#36c98e' : '#ff5c5c' }}>
          {fired ? 'TRADING HALTED' : 'EMERGENCY STOP'}
        </span>
      </div>
      <p style={{ margin: '0 0 12px', fontSize: 12, color: '#7e8a96', lineHeight: 1.5 }}>
        {fired
          ? 'Kill switch sent. All market making activity stopped. Restart the engine to resume.'
          : 'Immediately stops all market making activity and cancels pending quote generation. Does not cancel open orders already on the exchange.'}
      </p>
      {!fired && (
        <button
          onClick={handleStop}
          disabled={connectionStatus !== 'connected'}
          style={{
            padding: '8px 20px',
            borderRadius: 6,
            fontSize: 12,
            fontWeight: 700,
            cursor: connectionStatus === 'connected' ? 'pointer' : 'not-allowed',
            background: confirmed ? '#ff5c5c' : '#7f1d1d',
            color: '#fff',
            border: `1px solid ${confirmed ? '#ff5c5c' : '#991b1b'}`,
            transition: 'all 0.15s',
          }}
        >
          {confirmed ? '⚠ CONFIRM — STOP ALL TRADING' : 'Stop All Trading'}
        </button>
      )}
    </div>
  );
}

function ConfigRow({ label, children }: { label: React.ReactNode; children: React.ReactNode }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      padding: '9px 0', borderBottom: '1px solid #0a0d10',
    }}>
      <span style={{ fontSize: 13, color: '#d7dde4' }}>{label}</span>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>{children}</div>
    </div>
  );
}
